import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Map } from "immutable";
import { Col } from "react-bootstrap";
import { Field, FieldArray, formValueSelector, change } from "redux-form";

import { dollars } from "LIB/formatters/currency";
import { COMPANY, CONTACT } from "MODULES/companies/constants";
import * as actions from "MODULES/PurchaseOrders/actions";
import {
  form,
  Form as AppForm,
  FormInput,
  Select
} from "MODULES/shared/components/Forms";
import { BuyerField, CoBuyersFields } from "MODULES/PurchaseOrders/shared";
import ShippingFields from "MODULES/Orders/shared/ShippingFields";
import CompanyAddressWarning from "MODULES/shared/components/CompanyAddressWarning";
import BuyingStoreRadioField from "MODULES/Locations/components/BuyingStoreRadioField";
import {
  BUYER_CREDIT_PCT,
  FOB,
  LINE_ITEMS,
  MINIMUM_EXCHANGE_SRP,
  NAME,
  SETTINGS,
  SHIP_TO_COMPANY_ADDRESS_ID,
  SHIP_TO_COMPANY_ID,
  SHIPPING_ADDRESS,
  SHIPPING_METHOD,
  SHIPPING_TYPE
} from "APP_ROOT/constants";
import {
  BUYER_CREDIT_PCT_OPTIONS,
  PORTAL_PURCHASE,
  PURCHASE_FOR_REPAIR,
  PURCHASE_FOR_REPAIR_ASSIST,
  PURCHASE_FOR_SALE,
  PURCHASE_FOR_STOCK
} from "MODULES/PurchaseOrders/constants";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import {
  BUYER,
  CO_BUYER_ID,
  THIRD_BUYER_ID,
  USER
} from "MODULES/users/constants";
import PaymentTerms from "MODULES/shared/components/PaymentTerms/PaymentTerms";
import BioHazmatWarning from "MODULES/PurchaseOrders/shared/BioHazmatWarning";
import validate from "./validate";
import { TAGGED_BY_ID } from "../../../inventories/constants";
import LineItemRow from "./LineItemRow";

const formName = "incomingQuoteConvertToPurchaseOrders";

const legacyWarning = (
  <div className="alert alert-danger">
    <strong>Legacy Quote</strong> Company and contact must be assigned before
    quote can be converted to purchase order.
  </div>
);

class Form extends Component {
  static propTypes = {
    getFieldValue: PropTypes.func.isRequired,
    currentUser: PropTypes.instanceOf(Map),
    buyerCreditPct: PropTypes.string.isRequired,
    lineItems: PropTypes.arrayOf(Object),
    lineItemValues: PropTypes.arrayOf(Object),
    searchBuyers: PropTypes.func.isRequired,
    resetPurchaseFor: PropTypes.func.isRequired,
    setField: PropTypes.func.isRequired,
    subtotal: PropTypes.number.isRequired,
    company: PropTypes.instanceOf(Object),
    incomingQuote: PropTypes.instanceOf(Map),
    shippingType: PropTypes.string
  };

  state = {};

  componentWillReceiveProps(nextProps) {
    if (nextProps.shippingType !== this.props.shippingType) {
      const fields = [
        PURCHASE_FOR_STOCK,
        PURCHASE_FOR_REPAIR_ASSIST,
        PURCHASE_FOR_SALE,
        PURCHASE_FOR_REPAIR
      ];
      this.props.lineItemValues.forEach((item, i) => {
        if (item.included) {
          fields.filter(field => item[field]).forEach(field => {
            this.props.resetPurchaseFor(i, field);
          });
        }
      });
    }
  }

  companyInvalid() {
    return (
      !this.props.company ||
      !this.props.company.primary_physical_address ||
      !this.props.company.billing_address
    );
  }

  handleTaggedBy = company => {
    if (company && company.id) {
      this.props.change(TAGGED_BY_ID, company.id);
    }
  };

  toggleBuyer = buyer => this.setState(buyer);

  renderItemRows = ({ fields }) => {
    const { lineItems, subtotal } = this.props;

    return (
      <tbody>
        {fields.map((item, idx) => (
          <LineItemRow
            handleTaggedBy={this.handleTaggedBy}
            idx={idx}
            item={item}
            lineItems={lineItems}
          />
        ))}
        <tr style={{ backgroundColor: "transparent" }}>
          <td />
          <td className="text-right" colSpan="7">
            <h4 className="margin-top-none margin-bottom-none">
              Total:&nbsp;&nbsp;{dollars(subtotal)}
            </h4>
          </td>
          <td />
        </tr>
      </tbody>
    );
  };

  render() {
    const {
      buyerCreditPct,
      company,
      currentUser,
      getFieldValue,
      incomingQuote,
      lineItems,
      searchBuyers,
      setField,
      shippingType
    } = this.props;

    const showLegacyWarning =
      !incomingQuote.get(COMPANY) ||
      (!incomingQuote.get(CONTACT) && !incomingQuote.get(PORTAL_PURCHASE));

    return (
      <AppForm
        {...this.props}
        editable={!showLegacyWarning}
        disabled={this.companyInvalid()}
      >
        <AppForm.Body>
          {showLegacyWarning && legacyWarning}
          {!showLegacyWarning && <CompanyAddressWarning company={company} />}
          <div className="nav nav-tabs">
            <h4>Buying Information</h4>
          </div>
          <div
            className="row row-col-gutters-half"
            style={{ marginTop: "26px" }}
          >
            <Col md={3}>
              <Field
                label="Vendor"
                name="read_only_vendor"
                component={FormInput}
                readOnly
                disabled
                inputProps={{
                  value: incomingQuote.getIn([COMPANY, NAME])
                }}
              />
            </Col>
            <Col md={3}>
              <PaymentTerms />
            </Col>
            {currentUser.tenant.locations_count !== 1 && (
              <Col md={3}>
                <BuyingStoreRadioField />
              </Col>
            )}
          </div>
          <FormRow>
            <Col md={5}>
              <BuyerField
                searchBuyers={searchBuyers}
                selectedBuyer={
                  incomingQuote.get(BUYER) && incomingQuote.get(BUYER).toJS()
                }
              />
            </Col>
            <Col md={3}>
              <Field
                name={BUYER_CREDIT_PCT}
                className="text-right"
                component={Select}
                onChange={(_ev, val) => {
                  if (["25.0", "50.0", null].includes(val)) {
                    setField(THIRD_BUYER_ID)(null);
                    this.toggleBuyer({ selectedThirdBuyer: null });
                  }
                }}
                options={BUYER_CREDIT_PCT_OPTIONS}
                label="Buyer Credit"
                required
              />
            </Col>
          </FormRow>
          <FormRow>
            <CoBuyersFields
              buyerCreditPct={buyerCreditPct}
              searchBuyers={searchBuyers}
              selectedCoBuyer={this.state.selectedCoBuyer}
              selectedThirdBuyer={this.state.selectedThirdBuyer}
              numberOfBuyers={
                [
                  getFieldValue(CO_BUYER_ID),
                  getFieldValue(THIRD_BUYER_ID)
                ].filter(s => s).length + 1
              }
              toggleBuyer={this.toggleBuyer}
            />
          </FormRow>
          <hr className="hr-xs hr-dashed" />
          <div className="nav nav-tabs">
            <h4>Shipping Information</h4>
          </div>
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <Field name="fob" component={FormInput} label="FOB" required />
              </div>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name="ship_date"
                  component={FormInput}
                  type="date"
                  label="Ship Date"
                />
              </div>
            </Col>
          </FormRow>
          <FormRow>
            <Col xs={12}>
              <ShippingFields
                formName={formName}
                shippingFields={[
                  SHIPPING_TYPE,
                  SHIP_TO_COMPANY_ID,
                  SHIP_TO_COMPANY_ADDRESS_ID
                ]}
                setTenantAccount
                shippingMethod={SHIPPING_METHOD}
                setFOB={setField(FOB)}
                shippingAddressField={SHIPPING_ADDRESS}
                required
              >
                <BioHazmatWarning
                  parts={lineItems.map(l => l.part)}
                  shippingType={getFieldValue(SHIPPING_TYPE)}
                />
              </ShippingFields>
            </Col>
          </FormRow>
          <hr className="hr-sm" />
          <h4>Confirm Which Parts to Assign to PO</h4>
          <div
            className="row row-col-gutters-half"
            style={{ marginTop: "26px" }}
          >
            <Col md={12}>
              <table className="table table-striped table-numbered margin-bottom-none">
                <FieldArray
                  name={LINE_ITEMS}
                  component={this.renderItemRows}
                  shippingType={shippingType}
                />
              </table>
            </Col>
          </div>
        </AppForm.Body>
      </AppForm>
    );
  }
}

const selector = formValueSelector(formName);

export default connect(
  state => ({
    buyerCreditPct: selector(state, BUYER_CREDIT_PCT),
    getFieldValue: field => formValueSelector(formName)(state, field),
    currentUser: state.session.get(USER).toJS(),
    lineItemValues: selector(state, LINE_ITEMS),
    minExchangeSrp: state.session.getIn([SETTINGS, MINIMUM_EXCHANGE_SRP]),
    shippingType: selector(state, SHIPPING_TYPE)
  }),
  dispatch => ({
    resetPurchaseFor: (id, fieldName) =>
      dispatch(change(formName, `line_items[${id}].${fieldName}`, null)),
    searchBuyers: filter => dispatch(actions.searchBuyers(filter)),
    searchRepairStations: filter =>
      dispatch(actions.searchRepairStations(filter)),
    setField: field => val => dispatch(change(formName, field, val))
  })
)(
  form({
    form: formName,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/IncomingQuotes/Detail/ConvertToPurchaseOrder/Form.jsx